import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ProductFigures } from 'chery-contracts';
import { Printer } from 'lucide-react';
import { useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Notice } from '../../components/ui/Notice';
import { cn } from '../../lib/cn';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { metricText } from './board-display';
import { BoardHeader } from './BoardHeader';
import { BoardPeriodControls, useBoardPeriod } from './BoardPeriodControls';
import {
  ASKS,
  CHANNEL_MIX,
  INSIGHTS,
  INSIGHTS_TITLE,
  LAUNCH_PROGRAM_TITLE,
  LAUNCHES,
  MODELS,
  PLAN_2027_TITLE,
  PRODUCTS_INTRO,
  RETAIL_NETWORK,
} from './content/products';
import { figureOf, figuresByCode, figureState, figureText, modelsTotal, PRODUCT_METRICS, unboundCount } from './product-display';

/** The live figures strip of one product card (Phase 8): sold and delivered over the period; unbound → `—` + hint, never 0. */
const FiguresStrip = ({ figures, large = false }: { figures: ProductFigures | undefined; large?: boolean }) => {
  const { t } = useI18nTranslation();

  return (
    <dl
      className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2"
      data-product-figures
    >
      {PRODUCT_METRICS.map((metric) => {
        const figure = figures ? figureOf(figures, metric) : undefined;
        const state = figureState(figure);

        return (
          <div
            key={metric}
            data-figure={metric}
            data-state={state}
            className="min-w-0"
          >
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{t(`board.products.metrics.${metric}`)}</dt>
            <dd
              data-figure-value
              className={cn(
                'font-mono font-semibold tabular-nums',
                large ? 'text-2xl' : 'text-base',
                state === 'VALUE' ? 'text-foreground' : 'text-muted-foreground/70',
              )}
            >
              {figureText(figure)}
            </dd>
            {state === 'UNBOUND' ? (
              <dd
                data-figure-hint
                className="text-[10px] italic leading-tight text-muted-foreground"
              >
                {t('board.products.figures.unboundHint')}
              </dd>
            ) : state === 'BLANK' ? (
              <dd className="text-[10px] italic leading-tight text-muted-foreground">{t('board.products.figures.blankHint')}</dd>
            ) : null}
          </div>
        );
      })}
    </dl>
  );
};

/**
 * Page 03 — products, launch calendar, channel mix, insights and the retail network (static copy, no image slots),
 * and since Phase 8 a live figures strip on every model card: vehicles sold and delivered over the selected period
 * from `board.products` (the shared period shape, Year to date by default), printable with the shell hidden.
 */
export const ProductsPage = () => {
  const { t } = useI18nTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = MODELS.find((model) => model.id === selectedId) ?? null;
  const periodState = useBoardPeriod();
  const { period, rangeWarning } = periodState;
  const products = useQuery({
    ...orpc.board.products.queryOptions({ input: { period: period ?? { kind: 'YTD' } } }),
    enabled: period !== null,
    placeholderData: keepPreviousData,
  });
  const data = period ? products.data : undefined;
  const byCode = figuresByCode(data);
  const unbound = unboundCount(data);

  return (
    <div
      className="print-document space-y-8"
      data-board-page="products"
    >
      <BoardHeader {...PRODUCTS_INTRO} />

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{LAUNCH_PROGRAM_TITLE}</h2>
        <ol className="grid gap-2 sm:grid-cols-3 xl:grid-cols-6">
          {LAUNCHES.map((launch) => (
            <li
              key={`${launch.when}-${launch.what}`}
              className={cn(
                'rounded-md border border-border border-t-4 bg-card px-3 py-2.5',
                launch.lead ? 'border-t-primary' : 'border-t-primary/40',
              )}
              data-launch={launch.what}
            >
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{launch.when}</p>
              <p className="text-sm font-semibold text-foreground">{launch.what}</p>
              <p className="text-xs text-muted-foreground">{launch.note}</p>
            </li>
          ))}
        </ol>
      </section>

      <section
        className="space-y-3"
        data-products-section
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{PLAN_2027_TITLE}</h2>
            <p className="text-xs text-muted-foreground">
              {t('board.products.figures.subtitle')}
              {data ? (
                <span data-products-period>
                  {' · '}
                  {t('board.products.figures.periodLine', { period: data.period.label })}
                </span>
              ) : null}
            </p>
          </div>
          <div className="no-print flex flex-wrap items-center gap-2">
            <BoardPeriodControls
              state={periodState}
              resolved={data ? { fromWeekId: data.period.fromWeek.id, toWeekId: data.period.toWeek.id } : null}
            />
            <Button
              size="sm"
              disabled={!data}
              onClick={() => window.print()}
              data-print-products
            >
              <Printer className="size-3.5" />
              {t('board.products.figures.print')}
            </Button>
          </div>
        </div>

        {rangeWarning ? (
          <Notice
            tone="danger"
            className="no-print"
            onDismiss={periodState.clearWarning}
          >
            {rangeWarning}
          </Notice>
        ) : null}
        {products.isError ? (
          <Notice
            tone="danger"
            className="no-print"
          >
            {serverMessage(products.error) ?? t('board.loadFailed')}
          </Notice>
        ) : null}

        <p
          className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-muted-foreground"
          data-models-total
        >
          <span className="font-semibold uppercase tracking-wide">{t('board.products.figures.allModels')}</span>
          {PRODUCT_METRICS.map((metric) => (
            <span
              key={metric}
              data-models-total-metric={metric}
            >
              {t(`board.products.metrics.${metric}`)}{' '}
              <span className="font-mono font-semibold tabular-nums text-foreground">{metricText(modelsTotal(data, metric), 'NUMBER', null)}</span>
            </span>
          ))}
          {unbound ? (
            <span
              className="italic"
              data-products-unbound
            >
              {t('board.products.figures.unboundCount', unbound)}
            </span>
          ) : null}
        </p>
        {selected ? (
          <article
            className="space-y-3 rounded-lg border border-border bg-card p-5"
            data-model-detail={selected.id}
          >
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              onClick={() => setSelectedId(null)}
            >
              ← {t('board.products.close')}
            </button>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{selected.launch}</p>
            <h3 className="text-2xl font-semibold text-foreground">{selected.model}</h3>
            <p className="text-sm text-muted-foreground">
              {selected.segment} · {selected.role}
            </p>
            <p className="max-w-3xl text-sm leading-relaxed text-foreground">{selected.intro}</p>
            <p>
              <span className="font-mono text-3xl font-semibold tabular-nums text-foreground">{selected.units}</span>
              <span className="ms-2 text-xs text-muted-foreground">{t('board.products.plannedUnits')}</span>
            </p>
            <div className="max-w-md">
              <FiguresStrip
                figures={byCode.get(selected.code)}
                large
              />
            </div>
          </article>
        ) : (
          <ul className="print-product-grid grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {MODELS.map((model) => (
              <li
                key={model.id}
                className="print-avoid-break rounded-md border border-border bg-card"
                data-product-code={model.code}
              >
                <button
                  type="button"
                  className="flex w-full items-baseline justify-between gap-2 rounded-t-md px-3 pt-2.5 text-start hover:bg-muted"
                  onClick={() => setSelectedId(model.id)}
                  data-model={model.id}
                >
                  <span>
                    <span className="block text-sm font-semibold text-foreground">{model.model}</span>
                    <span className="block text-xs text-muted-foreground">
                      {model.role} · {model.segment}
                    </span>
                  </span>
                  <span
                    className="font-mono text-lg font-semibold tabular-nums text-foreground"
                    title={t('board.products.plannedUnits')}
                  >
                    {model.units}
                  </span>
                </button>
                <div className="px-3 pb-2.5">
                  <FiguresStrip figures={byCode.get(model.code)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section
          className="space-y-3 rounded-lg border border-border bg-card p-5"
          data-channel-mix
        >
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{CHANNEL_MIX.title}</h2>
          <div
            className="flex h-6 gap-0.5 overflow-hidden rounded-sm"
            role="img"
            aria-label={CHANNEL_MIX.channels.map((channel) => `${channel.name} ${channel.share}%`).join(', ')}
          >
            {CHANNEL_MIX.channels.map((channel, slot) => (
              <span
                key={channel.name}
                style={{ width: `${channel.share}%`, background: `var(--series-${slot + 1})` }}
              />
            ))}
          </div>
          <ul className="flex gap-4 text-sm text-foreground">
            {CHANNEL_MIX.channels.map((channel, slot) => (
              <li
                key={channel.name}
                className="flex items-center gap-1.5"
              >
                <span
                  aria-hidden
                  className="inline-block size-2.5 rounded-sm"
                  style={{ background: `var(--series-${slot + 1})` }}
                />
                {channel.name} {channel.share}%
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{CHANNEL_MIX.note}</p>
        </section>

        <section className="space-y-3 rounded-lg border border-border bg-card p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{INSIGHTS_TITLE}</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {INSIGHTS.map((insight) => (
              <li key={insight.stat}>
                <p className="font-mono text-2xl font-semibold tabular-nums text-foreground">{insight.stat}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{insight.note}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <section className="space-y-2 rounded-lg border border-border bg-card p-5">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{RETAIL_NETWORK.eyebrow}</p>
          <h2 className="text-lg font-semibold text-foreground">{RETAIL_NETWORK.title}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{RETAIL_NETWORK.body}</p>
        </section>
        <section className="space-y-2 rounded-lg border border-border bg-card p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{ASKS.title}</h2>
          <ul className="space-y-0.5 text-sm text-foreground">
            {ASKS.items.map((item) => (
              <li key={item}>· {item}</li>
            ))}
          </ul>
          <p className="text-xs leading-relaxed text-muted-foreground">{ASKS.note}</p>
        </section>
      </div>
    </div>
  );
};
